import { z } from 'zod';
import { HistoryEntrySchema, IDSchema, IsoTimestampSchema, MoneyCentsSchema, QuoteIdSchema, SchemaVersionSchema, } from './shared.js';
export const InvoiceStateSchema = z.enum([
    'draft',
    'sent',
    'partially_paid',
    'paid',
    'overdue',
    'voided',
]);
export const InvoiceSyncStatusSchema = z.enum(['in_sync', 'out_of_sync', 'sync_error']);
/**
 * Order-flow review decision 3 — how a payment arrived. `stripe` = card or bank
 * debit through Stripe; `check` / `bank_transfer` = recorded by staff with
 * "Mark paid outside Stripe", which marks the Stripe invoice paid out of band
 * so the payment still flows through the one Stripe paid path.
 */
export const InvoicePaymentMethodSchema = z.enum(['stripe', 'check', 'bank_transfer']);
/** The two methods staff can record with "Mark paid outside Stripe". */
export const OfflinePaymentMethodSchema = z.enum(['check', 'bank_transfer']);
export const InvoiceSchema = z.object({
    id: IDSchema,
    type: z.literal('invoice'),
    schemaVersion: SchemaVersionSchema,
    accountId: IDSchema,
    dealId: IDSchema,
    /**
     * RIG-444 — which system collected this invoice. `quickbooks` rows carry the
     * three QuickBooks identifiers; `stripe` rows carry `stripeInvoiceId`. A
     * CHECK constraint (migration 0058) stops either variant being written
     * half-populated, so the optionality below is discriminated in the database
     * rather than merely permitted here.
     */
    source: z.enum(['quickbooks', 'stripe']).default('quickbooks'),
    quickbooksInvoiceId: z.string().optional(),
    quickbooksInvoiceNumber: z.string().optional(),
    quickbooksCustomerId: z.string().optional(),
    /**
     * Order-flow review decision 3 — the QuickBooks Payment booked for a
     * Stripe-collected invoice. With `quickbooksInvoiceId` it records that the
     * payment reached the books; `syncStatus` is `in_sync` only once both exist.
     */
    quickbooksPaymentId: z.string().optional(),
    /** How the money arrived. Absent on rows recorded before migration 0075. */
    paymentMethod: InvoicePaymentMethodSchema.optional(),
    /** Check number or transfer reference entered with "Mark paid outside Stripe". */
    paymentReference: z.string().optional(),
    stripeInvoiceId: z.string().optional(),
    stripeHostedInvoiceUrl: z.string().optional(),
    /**
     * Authoritative linkage for a Stripe-collected invoice. The unit is the
     * submission, not the quote: a bundled proposal produces one combined
     * invoice.
     */
    submissionId: IDSchema.optional(),
    /** Convenience for a single-quote bundle; absent when the bundle has several. */
    quoteId: QuoteIdSchema.optional(),
    /**
     * The rep credited with commission for this invoice: the account owner at
     * the moment payment was confirmed. Absent on rows recorded before the
     * snapshot existed; the commission run then uses the account's current owner.
     */
    commissionRepEmail: z.string().optional(),
    state: InvoiceStateSchema,
    amount: MoneyCentsSchema,
    amountPaid: MoneyCentsSchema.default(0),
    amountDue: MoneyCentsSchema,
    currency: z.string().length(3).default('USD'),
    issuedAt: IsoTimestampSchema,
    dueAt: IsoTimestampSchema,
    paidAt: IsoTimestampSchema.optional(),
    voidedAt: IsoTimestampSchema.optional(),
    voidedReason: z.string().optional(),
    // SO-001 commission rebuild (A2) — refund tracking. A refund is money
    // returned to the customer AFTER payment; distinct from `voided` (cancelled
    // before/without payment). When `refundedAt` is set, the invoice's revenue is
    // excluded from commission so the pending statement cancels before payout
    // (no clawback — SO-001 §6.4 intent). `refundAmount` is informational; the
    // presence of `refundedAt` is the commission-cancel signal.
    refundedAt: IsoTimestampSchema.optional(),
    refundedBy: z.string().optional(),
    refundReason: z.string().optional(),
    refundAmount: MoneyCentsSchema.optional(),
    pdfDocumentId: IDSchema.optional(),
    lastSyncedAt: IsoTimestampSchema,
    syncStatus: InvoiceSyncStatusSchema.default('in_sync'),
    syncError: z.string().optional(),
    reminderFiredAt30dOverdue: IsoTimestampSchema.optional(),
    history: z.array(HistoryEntrySchema).default([]),
    createdAt: IsoTimestampSchema,
    createdBy: z.string(),
    updatedAt: IsoTimestampSchema,
    updatedBy: z.string(),
    deletedAt: IsoTimestampSchema.nullable().optional(),
    version: z.number().int().positive(),
});
export const INVOICE_STATE_DISPLAY = {
    draft: 'Draft',
    sent: 'Sent',
    partially_paid: 'Partially paid',
    paid: 'Paid',
    overdue: 'Overdue',
    voided: 'Voided',
};
// ─── Route-boundary schemas (T7-002) ────────────────────────────────────────
//
// These are NOT part of the Invoice entity definition; they describe the HTTP
// surface only. Kept in this file (rather than a separate `invoice-routes.ts`)
// because the existing repo convention is single-file `<entity>.ts` schemas
// (see quote.ts, submission.ts, deal.ts). Adding a sibling file just for
// route shapes would diverge from that convention.
/**
 * Query parameters accepted by `GET /api/v1/invoices`.
 *
 * Per T7-002 contract:
 *   - Invoices are scoped to Accounts or Deals — no global list view in V1.
 *     Callers MUST supply `accountId` OR `dealId` (route returns 400 if
 *     neither is present).
 *   - `state` filter applies in-memory after the repository call (the
 *     repository methods don't accept a state filter today — kept narrow
 *     per T7-001's mirror-entity discipline).
 *   - `limit` is advisory; current repository methods return all live rows
 *     for an account/deal. Pagination is a V1.5 concern.
 */
export const InvoiceListQuerySchema = z.object({
    accountId: IDSchema.optional(),
    dealId: IDSchema.optional(),
    state: InvoiceStateSchema.optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
});
/**
 * Path params for `GET /api/v1/invoices/:id` and `DELETE /api/v1/invoices/:id`.
 *
 * The id pattern matches the generator in repositories/_id.ts:
 *   INV-<unix-ms>-<4-char-rand>  (e.g. INV-1714780800000-a3b9)
 *
 * The regex deliberately accepts the broader IDSchema-compatible shape so
 * future ID generations (e.g., 6-char rand for high-volume mirror writes)
 * remain valid.
 */
export const InvoiceIdParamSchema = z.object({
    id: z.string().regex(/^INV-\d+-[a-z0-9]{4,6}$/),
});
/**
 * Request body for `DELETE /api/v1/invoices/:id` (soft-delete).
 *
 * superAdmin-only per T7-002 contract + the file-header doctrine in
 * `repositories/invoice.ts`: mirror entities expose no direct-user-mutation
 * path; soft-delete is the single exception, and only superAdmin may use it.
 */
export const InvoiceDeleteBodySchema = z.object({
    expectedVersion: z.number().int().positive(),
});
/**
 * Response body for `GET /api/v1/invoices` (list).
 */
export const InvoiceListResponseSchema = z.object({
    invoices: z.array(InvoiceSchema),
});
/**
 * Response body for `DELETE /api/v1/invoices/:id` returning 412.
 *
 * Shape mirrors the optimisticUpdate primitive's `version_conflict` envelope
 * (per T1-054 doctrine + `packages/backend/src/primitives/optimistic-update.ts`).
 * The UI surfaces `current` for diff-rendering on the resolve-conflict screen.
 */
export const InvoiceVersionConflictResponseSchema = z.object({
    reason: z.literal('version_conflict'),
    current: InvoiceSchema,
});
//# sourceMappingURL=invoice.js.map